import { useEffect, useState } from 'react';
import { Box, Chip, MenuItem, Select, SelectChangeEvent, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../../../components/ui/Section';
import { SupplierEsgProfile } from '../../../../../mocks/data/supplierEsgDashboardData';
import { applyRoundAndFormat } from '../../../../../utils';
import useCurrencyCodeConfiguration from '../../../../../hooks/configurations/useCurrencyCodeConfiguration';

export interface SupplierComparisonTabProps {
  profiles: SupplierEsgProfile[];
}

const MAX_COMPARISON = 4;

const SupplierComparisonTab: React.FC<SupplierComparisonTabProps> = ({ profiles }) => {
  const { t } = useTranslation('esgDashboard');
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const [selectedIds, setSelectedIds] = useState<number[]>(profiles.slice(0, 3).map((p) => p.supplierId));

  useEffect(() => {
    getCurrencyCodeConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Keep the selection valid whenever the page-level filters change the available suppliers.
    setSelectedIds((prev) => {
      const stillValid = prev.filter((id) => profiles.some((p) => p.supplierId === id));
      return stillValid.length > 0 ? stillValid : profiles.slice(0, 3).map((p) => p.supplierId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles]);

  const handleChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value as number[];
    setSelectedIds(value.slice(0, MAX_COMPARISON));
  };

  const selectedProfiles = profiles.filter((p) => selectedIds.includes(p.supplierId));

  // eslint-disable-next-line no-unused-vars
  const rows: { label: string; render: (_p: SupplierEsgProfile) => React.ReactNode }[] = [
    { label: t('common.esgScore', 'ESG score'), render: (p) => p.esgScore },
    { label: t('comparison.environmentalScore', 'Environmental score'), render: (p) => p.environmentalScore },
    { label: t('comparison.socialScore', 'Social score'), render: (p) => p.socialScore },
    { label: t('comparison.governanceScore', 'Governance score'), render: (p) => p.governanceScore },
    { label: t('filters.riskLevel', 'Risk level'), render: (p) => <Chip label={p.riskLevel}
      size="small"
      color={p.riskLevel === 'Low' ? 'success' : p.riskLevel === 'Medium' ? 'warning' : 'error'}
      variant="outlined" /> },
    { label: t('comparison.co2ePerYear', 'CO\u2082e (tonnes/yr)'), render: (p) => p.co2eTonnesPerYear },
    { label: t('comparison.regulatoryCompliance', 'Regulatory compliance'), render: (p) => p.regulatoryComplianceStatus },
    { label: t('comparison.qualityScore', 'Quality score'), render: (p) => p.qualityScore.toFixed(1) },
    { label: t('operational.onTimeDelivery', 'On-time delivery'), render: (p) => `${p.onTimeDeliveryPercentage}%` },
    { label: t('comparison.sustainabilityCertifications', 'Sustainability certifications'), render: (p) => p.sustainabilityCertifications.join(', ') || t('common.none', 'None') },
    { label: t('comparison.governanceCertifications', 'Governance certifications'), render: (p) => p.governanceCertifications.join(', ') || t('common.none', 'None') },
    { label: t('comparison.annualSpend', 'Annual spend'), render: (p) => applyRoundAndFormat(p.annualSpend, currencyCode, 0) },
  ];

  return (
    <Stack spacing={3}>
      <Select
        multiple
        size="small"
        value={selectedIds}
        onChange={handleChange}
        sx={{ maxWidth: 480 }}
        renderValue={(selected) => (selected as number[])
          .map((id) => profiles.find((p) => p.supplierId === id)?.name)
          .filter(Boolean)
          .join(', ')}
      >
        {profiles.map((p) => (
          <MenuItem key={p.supplierId}
            value={p.supplierId}
            disabled={!selectedIds.includes(p.supplierId) && selectedIds.length >= MAX_COMPARISON}>
            {p.name}
          </MenuItem>
        ))}
      </Select>

      <Section style={{ boxShadow: 'none' }}>
        <Box sx={{ p: 4 }}>
          {selectedProfiles.length === 0 ? (
            <Typography variant="textSM"
              color="text.secondary">{t('comparison.selectPrompt', 'Select up to {{max}} suppliers to compare.', { max: MAX_COMPARISON })}</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('comparison.metric', 'Metric')}</TableCell>
                  {selectedProfiles.map((p) => (
                    <TableCell key={p.supplierId}
                      align="right">{p.name}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.label}>
                    <TableCell>{row.label}</TableCell>
                    {selectedProfiles.map((p) => (
                      <TableCell key={p.supplierId}
                        align="right">{row.render(p)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Section>
    </Stack>
  );
};

export default SupplierComparisonTab;
